import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import clickIcon from "./../../media/orange-sort-icon.svg";
import "../../styleSheet/main/components/TODO/managerTODO/managerTODO.css";
import ButtonDelete from "./../../media/delete-button.svg";
import DirectionIcon from "./../../media/direction-icon.svg";
import DirectionIconOrange from "./../../media/orange-sort-icon.svg";
import HistoryManagerTODO from "./historyManagerTODO";
import httpClient from "../../services/httpClient";

const ManagerTODO = () => {
  const { t } = useTranslation();
  const [managers, setManagers] = useState([]);
  const [selectedManager, setSelectedManager] = useState(null);
  const [selectedManagerTasks, setSelectedManagerTasks] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isHistoryMode, setIsHistoryMode] = useState(false);
  const [hover, setHover] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);

  // Fetch managers with task counts on component mount
  useEffect(() => {
    fetchManagers();
  }, []);

  const fetchManagers = async () => {
    try {
      setLoading(true);
      const response = await httpClient.get("/ToDoTasks/assigned-to-me/count", {
        withAuth: true,
      });
      setManagers(response.data);
    } catch (error) {
      console.error("Error fetching managers:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTasksForManager = async (userId) => {
    try {
      setLoading(true);
      const response = await httpClient.get(
        `/ToDoTasks/assigned-to-me/by-user/${userId}`,
        { withAuth: true },
      );
      // Ensure tasks without status are handled correctly
      const tasks = (response.data || []).map((task) => ({
        ...task,
        status: task.status || null, // Explicitly set null for tasks without status
      }));
      setSelectedManagerTasks(tasks);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleManagerClick = (manager) => {
    setSelectedManager(manager);
    setIsHistoryMode(false);
    fetchTasksForManager(manager.userId);
  };

  const handleBackClick = () => {
    setSelectedManager(null);
    setSelectedManagerTasks([]);
    setIsHistoryMode(false);
  };

  const handleViewHistory = () => {
    setIsHistoryMode(true);
  };

  const handleExitHistory = () => {
    setIsHistoryMode(false);
  };

  const getFirstName = (fullName) => {
    return fullName.split(" ")[0];
  };

  const formatDate = (date) => {
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return new Date(date).toLocaleDateString("pl-PL", options);
  };

  // Map API status to Polish display text
  const getStatusDisplay = (apiStatus) => {
    const statusMap = {
      NotStarted: "Zaczęte",
      InProgress: "W trakcie",
      Completed: "Skończone",
    };
    return statusMap[apiStatus] || apiStatus;
  };

  const openEditModal = (task) => {
    // Convert API status to Polish display text for the modal
    // If status is null/undefined/empty, don't set a status (new task)
    const displayStatus = task.status ? getStatusDisplay(task.status) : "";
    const taskWithDisplayStatus = {
      ...task,
      status: displayStatus,
    };
    setSelectedTask(taskWithDisplayStatus);
    setComment(task.comment || "");
    setShowEditModal(true);
  };

  const handleCommentChange = (e) => {
    setComment(e.target.value);
  };

  const saveChanges = async () => {
    if (!selectedTask) return;

    try {
      // Map Polish UI status to API enum values
      const statusMapping = {
        Zaczęte: "NotStarted",
        "W trakcie": "InProgress",
        Skończone: "Completed",
      };

      const taskStatus = selectedTask.status
        ? statusMapping[selectedTask.status] || selectedTask.status
        : "NotStarted";

      await httpClient.put(
        `/ToDoTasks/${selectedTask.id}/for-assignee`,
        {
          comment: comment,
          taskStatus: taskStatus,
        },
        { withAuth: true },
      );

      // Refresh tasks for the manager
      await fetchTasksForManager(selectedManager.userId);
      closeEditModal();
    } catch (error) {
      console.error("Error saving task:", error);
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedTask(null);
  };

  const handleStatusChange = (status) => {
    if (!selectedTask) return;

    // Update the selected task's status locally for immediate UI feedback
    setSelectedTask((prev) => ({ ...prev, status }));
  };

  const getDateLabel = (taskDate) => {
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    const taskDateObj = new Date(taskDate);

    if (taskDateObj < today.setHours(0, 0, 0, 0)) {
      return {
        label: taskDateObj.toLocaleDateString("pl-PL", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
        isPast: true,
      };
    } else if (taskDateObj.toDateString() === today.toDateString()) {
      return { label: "na dzisiaj", isPast: false };
    } else if (taskDateObj.toDateString() === tomorrow.toDateString()) {
      return { label: "na jutro", isPast: false };
    } else {
      return {
        label: taskDateObj.toLocaleDateString("pl-PL", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
        isPast: false,
      };
    }
  };

  const groupedTasks =
    selectedManagerTasks?.reduce((acc, task) => {
      const { label, isPast } = getDateLabel(task.dueDate);
      if (!acc[label]) {
        acc[label] = [];
      }
      acc[label].push({ ...task, isPast });
      return acc;
    }, {}) || {};

  const sortedLabels = [
    ...Object.keys(groupedTasks)
      .filter((label) => groupedTasks[label][0]?.isPast)
      .sort((a, b) => new Date(a) - new Date(b)), // Przeszłe na czerwono
    "na dzisiaj",
    "na jutro",
    ...Object.keys(groupedTasks)
      .filter(
        (label) =>
          !["na dzisiaj", "na jutro"].includes(label) &&
          !groupedTasks[label][0]?.isPast,
      )
      .sort((a, b) => new Date(a) - new Date(b)), // Przyszłe
  ];

  const renderEditModal = () => (
    <div className="modal-overlay">
      <div className="modal-content-TODO">
        <img
          src={ButtonDelete}
          className="close-button-TODO"
          onClick={closeEditModal}
          alt="Close"
        />
        <p>{t("todo.infoAboutTask")}</p>
        <div className="form-group">
          <div className="left">
            <div className="think">{t("todo.content")}</div>
          </div>
          <p>{selectedTask ? selectedTask.title : ""}</p>
        </div>
        <div className="form-group">
          <div className="left">
            <div className="think">{t("todo.date")}</div>
          </div>
          <p>{selectedTask ? formatDate(selectedTask.dueDate) : ""}</p>
        </div>
        <div className="form-group">
          <div className="left">
            <div className="think">{t("todo.comment")}</div>
          </div>
          <textarea
            value={comment} // Komentarz powiązany ze stanem
            onChange={handleCommentChange}
            rows="4"
            placeholder={t("todo.addCommentPlaceholder")}
          />
        </div>
        {/* <div className="modal-info">
          <div className="comment-section">
            <strong>Komentarz :</strong>
            <textarea
              value={comment}
              onChange={handleCommentChange}
              placeholder="Dodaj komentarz do zadania"
            />
          </div>
        </div> */}
        <div className="status-options">
          <div
            className="status-option"
            onClick={() => handleStatusChange("Zaczęte")}
          >
            <div
              className={`square ${
                selectedTask?.status === "Zaczęte" ? "started" : ""
              }`}
            ></div>
            <span>{t("todo.status.started")}</span>
          </div>
          <div
            className="status-option"
            onClick={() => handleStatusChange("W trakcie")}
          >
            <div
              className={`square ${
                selectedTask?.status === "W trakcie" ? "in-progress" : ""
              }`}
            ></div>
            <span>{t("todo.status.inProgress")}</span>
          </div>
          <div
            className="status-option"
            onClick={() => handleStatusChange("Skończone")}
          >
            <div
              className={`square ${
                selectedTask?.status === "Skończone" ? "completed" : ""
              }`}
            ></div>
            <span>{t("todo.status.completed")}</span>
          </div>
        </div>
        <div className="modal-buttons">
          <button className="save-button" onClick={saveChanges}>
            {t("todo.saveChanges")}
          </button>
        </div>
      </div>
    </div>
  );

  if (selectedManager) {
    return (
      <>
        <div className="manager-todo-container">
          {!isHistoryMode && (
            <div className="todo-for">
              <p>{selectedManager.userFullName}</p>
            </div>
          )}
          <img
            src={clickIcon}
            alt={t("todo.backToList")}
            className="back-icon"
            onClick={handleBackClick}
          />

          {!isHistoryMode ? (
            <>
              {loading ? (
                <p>{t("common.loading")}</p>
              ) : selectedManagerTasks.length === 0 ? (
                <div className="not-yet">
                  <p className="no-tasks">
                    {t("todo.noTasksFromManager", {
                      name: selectedManager.userFullName,
                    })}
                  </p>
                </div>
              ) : (
                sortedLabels.map((label, index) => {
                  if (!groupedTasks[label]?.length) return null;
                  return (
                    <div key={index}>
                      <strong
                        style={{
                          color: groupedTasks[label][0]?.isPast
                            ? "red"
                            : "inherit",
                        }}
                      >
                        {label}:
                      </strong>
                      {groupedTasks[label]?.map((task, taskIndex) => (
                        <div
                          key={taskIndex}
                          className={`task ${task.isPast ? "overdue" : ""}`}
                          onClick={() => openEditModal(task)}
                        >
                          <div
                            className={`square ${
                              !task.status ||
                              task.status === null ||
                              task.status === undefined ||
                              task.status === ""
                                ? ""
                                : task.status === "Completed"
                                  ? "completed"
                                  : task.status === "InProgress"
                                    ? "in-progress"
                                    : task.status === "NotStarted"
                                      ? "started"
                                      : ""
                            }`}
                          ></div>
                          <span
                            className={
                              task.status === "Completed" ? "strikethrough" : ""
                            }
                          >
                            {task.title}
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                })
              )}
              <div className="header-buttons">
                <button
                  className="booking-button"
                  onMouseEnter={() => setHover(true)}
                  onMouseLeave={() => setHover(false)}
                  onClick={handleViewHistory}
                >
                  <div
                    className="icon"
                    style={{
                      backgroundImage: `url(${
                        hover ? DirectionIconOrange : DirectionIcon
                      })`,
                      transform: hover ? "scale(1.2)" : "rotate(270deg)",
                    }}
                  ></div>
                  {t("todo.history")}
                </button>
              </div>
            </>
          ) : (
            <>
              <HistoryManagerTODO userId={selectedManager.userId} />
              <div className="creating">
                <button onClick={handleExitHistory} className="create-button">
                  {t("todo.backToManagerTodo", {
                    name: getFirstName(selectedManager.userFullName),
                  })}
                </button>
              </div>
            </>
          )}
        </div>

        {showEditModal && renderEditModal()}
      </>
    );
  }
  const sortedManagers = [...managers].sort(
    (a, b) => b.taskCount - a.taskCount,
  );

  return (
    <div className="manager-todo-container">
      <p>{t("todo.listFromManagers")}</p>
      {loading ? (
        <p>{t("common.loading")}</p>
      ) : (
        <ul className="manager-list">
          {sortedManagers.map((manager, index) => (
            <li
              className="manager-item"
              key={index}
              onClick={() => handleManagerClick(manager)}
            >
              <img src={clickIcon} alt="Ikona" className="manager-icon" />
              <span>{manager.userFullName}</span>
              {manager.taskCount > 0 && (
                <div className="manager-circle">{manager.taskCount}</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ManagerTODO;
